/**
 * Whether this document is running inside someone else's frame — the QUIT
 * button's gate (bug report item 4: "the quit button in the main menu should
 * navigate to https://this.os.isfine.org/… but only if the page is not in an
 * iframe"). Top-level navigation is only this port's call to make when the
 * tab *is* the top-level browsing context; a page another site chose to
 * embed must not hijack it out from under them.
 *
 * `window.top` is readable cross-origin by spec (that is how frame-busting
 * scripts have always compared it to `window.self`), but a stricter embed —
 * `sandbox` without `allow-same-origin` — can make even that read throw a
 * `SecurityError`. A throw is treated the same as "framed": it is the safer
 * assumption either way, and it is certainly not "definitely not framed".
 *
 * `win` defaults to the real global — via a `typeof` guard rather than a
 * bare `window` reference in the parameter list, so importing (or unit
 * testing) this module never requires a DOM to exist. No window at all is
 * one more case with no safe top-level navigation, so it counts as framed too.
 */
export function isFramed(win?: Window): boolean {
  const target = win ?? (typeof window === "undefined" ? undefined : window);
  if (!target) return true;
  try {
    return target.self !== target.top;
  } catch {
    return true;
  }
}
